import type { ReactNode, JSX} from "react";
import { cn, gapClass, pxClass, pyClass, pClass } from "./cn";

export interface StackProps {
  /** Gap between children (Tailwind spacing scale: 0, 0.5, 1, 2, 3…) */
  readonly gap?: number;
  /** Uniform padding */
  readonly p?: number;
  /** Horizontal padding */
  readonly px?: number;
  /** Vertical padding */
  readonly py?: number;
  /** Take remaining flex space */
  readonly flex1?: boolean;
  /** Truncate overflowing text children */
  readonly truncate?: boolean;
  /** Cross-axis alignment */
  readonly align?: "start" | "center" | "end" | "stretch";
  /** Main-axis justification */
  readonly justify?: "start" | "center" | "end" | "between";
  /** Full height */
  readonly fullHeight?: boolean;
  /** Extra className (positioning, margins only) */
  readonly className?: string;
  readonly children: ReactNode;
}

const alignMap: Readonly<Record<string, string>> = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
};

const justifyMap: Readonly<Record<string, string>> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
};

export function Stack({
  gap,
  p,
  px,
  py,
  flex1,
  truncate,
  align,
  justify,
  fullHeight,
  className,
  children,
}: StackProps): JSX.Element {
  return (
    <div
      className={cn(
        "flex flex-col",
        gapClass(gap),
        pClass(p),
        pxClass(px),
        pyClass(py),
        flex1 && "flex-1 min-w-0",
        truncate && "min-w-0",
        align && alignMap[align],
        justify && justifyMap[justify],
        fullHeight && "h-full",
        className,
      )}
    >
      {children}
    </div>
  );
}
